import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Divider,
  HorizontalStack,
  Text,
  useBreakpoints,
} from '@shopify/polaris';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import DatePresetPicker from '../../../UI/Common/DatePresetPicker';
import DepositScopeFilter from '../../../UI/Reports/DepositScopeFilter';
import FilterSelect from '../../../UI/Products/FilterSelect';
import { DEPOSIT_TYPE_OPTIONS, buildDepositScopeLabel } from '../../../../helpers/depositTypes';
import { ActiveFilterChips } from '../efficacy/ActiveFilterChips';
import { COMPARE_SHORTCUTS, DATE_SHORTCUTS } from './useStocksFilters';

// Sin selección de ámbito el back valora TODOS los depósitos operables:
// el selector no pre-marca nada (en eficacia pre-marca el perímetro de venta).
const NO_DEFAULT_ROLES = [];

const splitIds = (v) => (v && v !== 'ALL' ? v.split(':').filter(Boolean) : []);

/**
 * Fechas (valoración y comparación opcional), ámbito de depósitos y
 * filtros de catálogo. Debajo, los chips de lo que está filtrado.
 */
export const StocksFiltersCard = ({ f, timezone }) => {
  const { lgUp } = useBreakpoints();

  const datePresets = useMemo(
    () =>
      DATE_SHORTCUTS.map((s) => ({
        key: s.key,
        label: s.key === 'today' ? 'Hoy (saldo actual)' : s.label,
        value: s.compute(timezone),
      })),
    [timezone],
  );

  const comparePresets = useMemo(() => {
    const base = f.date ? DateTime.fromISO(f.date, { zone: timezone }) : DateTime.now().setZone(timezone);
    return COMPARE_SHORTCUTS.map((s) => ({ key: s.key, label: s.label, value: s.compute(base) }));
  }, [f.date, timezone]);

  const toggleCompare = (checked) => {
    if (!checked) f.setCompareDate('');
    else f.setCompareDate(comparePresets[0].value);
  };

  const depositsLabel = buildDepositScopeLabel({
    depositsValue: f.depositsValue,
    depositRolesValue: f.depositRolesValue,
    depositGroupsValue: f.depositGroupsValue,
    groupOptions: f.groupOptions,
  });

  return (
    <Card roundedAbove="sm">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'space-between' }}>
        <Box>
          <Text variant="headingMd" as="h4">
            Fechas
          </Text>
          <Box minHeight=".5rem" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-end' }}>
            <div>
              <Text as="p" variant="bodySm" color="subdued">
                Fecha de valoración
              </Text>
              <Box minHeight=".25rem" />
              <DatePresetPicker label="Fecha de valoración" value={f.date} onChange={f.setDate} presets={datePresets} />
            </div>
            <div>
              <Checkbox label="Comparar con" checked={f.hasCompare} onChange={toggleCompare} />
              <Box minHeight=".25rem" />
              <DatePresetPicker
                label="Elegir fecha"
                value={f.compareDate}
                onChange={(v) => f.setCompareDate(v)}
                presets={comparePresets}
                disabled={!f.hasCompare}
              />
            </div>
          </div>
        </Box>
        <HorizontalStack align={lgUp ? 'end' : 'start'}>
          <Box>
            <Text variant="headingMd" as="h4">
              Ámbito y categorización
            </Text>
            <Box minHeight=".5rem" />
            <ButtonGroup segmented={lgUp}>
              <DepositScopeFilter
                title={depositsLabel}
                allSelected={f.depositsValue === 'ALL'}
                roles={f.depositRolesValue ? f.depositRolesValue.split(':').filter(Boolean) : []}
                groups={f.depositGroupsValue ? f.depositGroupsValue.split(':').filter(Boolean) : []}
                deposits={splitIds(f.depositsValue)}
                roleOptions={DEPOSIT_TYPE_OPTIONS}
                groupOptions={f.groupOptions}
                depositOptions={f.depositOptions}
                onChange={f.setDepositScope}
                defaultRoles={NO_DEFAULT_ROLES}
              />
              {Object.values(f.filters)
                .filter((x) => x.bundle)
                .map((x) => (
                  <FilterSelect
                    {...x}
                    options={x.options ?? []}
                    key={x.slug}
                    defaultValue={x.value ?? ''}
                    onFilterChange={f.handleFilter}
                  />
                ))}
            </ButtonGroup>
          </Box>
        </HorizontalStack>
      </div>
      <Box minHeight=".75rem" />
      <Divider />
      <Box minHeight=".5rem" />
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0 }}>
          <ActiveFilterChips
            filters={f.filters}
            depositsValue={f.depositsValue}
            depositRolesValue={f.depositRolesValue}
            depositGroupsValue={f.depositGroupsValue}
            depositOptions={f.depositOptions}
            groupOptions={f.groupOptions}
          />
        </div>
        <Button onClick={f.clearFilters} plain>
          Borrar filtros
        </Button>
      </div>
    </Card>
  );
};
